import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { NoteMention, NoteTextSegment, NoteUser } from '../account-notes/account-notes.types';
import {
    insertMention,
    keptMentions,
    mentionQueryAt,
    MentionQuery,
    mentionSegments,
    removeMentionBefore,
} from './mention-text';

const FLYOUT_WIDTH = 224;
const FLYOUT_MAX_HEIGHT = 240;
const FLYOUT_GAP = 8;

/**
 * A comment text field with `@` mentions: the name list of the CPMM annotations (↑/↓, Enter or Tab
 * picks, Esc closes only the list) and picked names coloured inside the field.
 *
 * The field stays a `textarea`; a layer behind it draws the same text with the mentions coloured, and
 * the field's own text is transparent. Controlled: `value` and `mentions` come in, every change goes out.
 */
@Component({
    selector: 'abcp-mention-input',
    templateUrl: './mention-input.component.html',
    styleUrls: ['./mention-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AbcpMentionInputComponent {
    @Input() value = '';
    /** The people mentioned in `value` — only these are coloured and notified. */
    @Input() mentions: NoteMention[] = [];
    /** Who can be mentioned. */
    @Input() users: NoteUser[] = [];
    /** Not in the list: mentioning yourself does nothing. */
    @Input() currentUserId: string | null = null;
    @Input() placeholder = '';
    @Input() rows = 2;
    /** `reply`: the round one-line field under a comment. */
    @Input() variant: 'field' | 'reply' = 'field';
    /** Enter (without Shift) sends instead of starting a new line. */
    @Input() submitOnEnter = false;
    @Input() fieldTestId: string | null = null;

    @Output() valueChange = new EventEmitter<string>();
    @Output() mentionsChange = new EventEmitter<NoteMention[]>();
    @Output() enterPressed = new EventEmitter<void>();

    @ViewChild('field') private fieldRef?: ElementRef<HTMLTextAreaElement>;
    @ViewChild('layer') private layerRef?: ElementRef<HTMLElement>;

    listOpen = false;
    options: NoteUser[] = [];
    activeIndex = 0;
    listPosition: { top: number; left?: number; right: number; isAbove: boolean } = {
        top: 0,
        right: 0,
        isAbove: false,
    };

    private query: MentionQuery | null = null;

    get segments(): NoteTextSegment[] {
        return mentionSegments(this.value, this.mentions);
    }

    onInput(text: string): void {
        this.setValue(text);
        this.refreshList();
    }

    onKeydown(event: KeyboardEvent): void {
        // While an IME composes (Japanese, Chinese, dead keys), Enter confirms the composition: the
        // keys belong to the IME, not to sending or the name list.
        if (event.isComposing) {
            return;
        }
        if (this.listOpen) {
            this.onListKey(event);
            return;
        }
        if (event.key === 'Backspace') {
            this.removeWholeMention(event);
        } else if (event.key === 'Enter' && !event.shiftKey && this.submitOnEnter) {
            event.preventDefault();
            this.enterPressed.emit();
        }
    }

    /** A moved caret can enter or leave an `@…`; Enter, Esc and the list keys are handled on keydown. */
    onKeyup(event: KeyboardEvent): void {
        if (!['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab'].includes(event.key)) {
            this.onCaretMove();
        }
    }

    onCaretMove(): void {
        this.refreshList();
    }

    syncScroll(): void {
        const field = this.fieldRef?.nativeElement;
        const layer = this.layerRef?.nativeElement;
        if (field && layer) {
            layer.scrollTop = field.scrollTop;
        }
    }

    /** Keeps the focus in the field while an option is clicked. */
    onListMousedown(event: MouseEvent): void {
        event.preventDefault();
    }

    pick(user: NoteUser): void {
        const field = this.fieldRef?.nativeElement;
        if (!field || !this.query) {
            return;
        }
        const next = insertMention(this.value, this.query, field.selectionStart, user.displayName);
        const mention = { userId: user.userId, displayName: user.displayName };
        this.closeList();
        field.value = next.text;
        field.setSelectionRange(next.caret, next.caret);
        field.focus();
        this.setValue(next.text, [...this.mentions, mention]);
    }

    closeList(): void {
        this.listOpen = false;
        this.query = null;
    }

    private onListKey(event: KeyboardEvent): void {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.activeIndex = Math.min(this.activeIndex + 1, this.options.length - 1);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.activeIndex = Math.max(this.activeIndex - 1, 0);
        } else if (event.key === 'Enter' || event.key === 'Tab') {
            // Picks the name: no send, no new line.
            event.preventDefault();
            this.pick(this.options[this.activeIndex]);
        } else if (event.key === 'Escape') {
            // Closes only the list; the composer or the edit stays open.
            event.preventDefault();
            event.stopPropagation();
            this.closeList();
        }
    }

    private removeWholeMention(event: KeyboardEvent): void {
        const field = event.target as HTMLTextAreaElement;
        if (field.selectionStart !== field.selectionEnd) {
            return;
        }
        const next = removeMentionBefore(this.value, field.selectionStart, this.mentions);
        if (next === null) {
            return;
        }
        event.preventDefault();
        field.value = next.text;
        field.setSelectionRange(next.caret, next.caret);
        this.setValue(next.text);
    }

    private setValue(text: string, mentions: NoteMention[] = this.mentions): void {
        const kept = keptMentions(text, mentions);
        this.value = text;
        this.valueChange.emit(text);
        if (kept.length !== this.mentions.length || kept.some((m, i) => m.userId !== this.mentions[i]?.userId)) {
            this.mentions = kept;
            this.mentionsChange.emit(kept);
        }
    }

    private refreshList(): void {
        const field = this.fieldRef?.nativeElement;
        this.query = field ? mentionQueryAt(this.value, field.selectionStart) : null;
        const query = this.query?.query.toLowerCase() ?? null;
        const options =
            query === null
                ? []
                : this.users.filter(
                      (u) => u.userId !== this.currentUserId && u.displayName.toLowerCase().includes(query),
                  );
        if (!field || options.length === 0) {
            this.closeList();
            return;
        }
        if (options.length !== this.options.length || !this.listOpen) {
            this.activeIndex = 0;
        }
        this.options = options;
        this.listPosition = positionBelowOrAbove(field.getBoundingClientRect());
        this.listOpen = true;
    }
}

/** Below the field, or above it when there is no room below — the CPMM annotations' placement. */
function positionBelowOrAbove(rect: DOMRect): { top: number; left?: number; right: number; isAbove: boolean } {
    const fitsRight = rect.left + FLYOUT_WIDTH <= window.innerWidth - FLYOUT_GAP;
    const horizontal = fitsRight ? { left: rect.left, right: 0 } : { right: FLYOUT_GAP };
    if (rect.bottom + FLYOUT_GAP + FLYOUT_MAX_HEIGHT > window.innerHeight - FLYOUT_GAP) {
        // `translateY(-100%)` in the template lifts the list by its own height, so it sits right above.
        return { top: rect.top - FLYOUT_GAP, ...horizontal, isAbove: true };
    }
    return { top: rect.bottom + FLYOUT_GAP, ...horizontal, isAbove: false };
}
